import type { StateCreator } from 'zustand/vanilla';
import type { ElementType, ViduV1ElementDetail } from '@/app/types';

type Reference = ViduV1ElementDetail;

export type FromPage =
  | 'home'
  | 'explore'
  | 'reference_tool'
  | 'reference_asset'
  | 'reference_asset_details'
  | 'reference_tool_details'
  | 'my_likes_page'
  | 'guest_profile' // 客态个人主页
  | 'my_published_page'
  | 'others'
  | 'profile';

/**
 * 主体弹窗模式
 *  view: 查看
 *  edit: 编辑
 *  create: 创建
 *  post: 投稿
 */
export type ReferenceModalMode = 'view' | 'edit' | 'create' | 'post' | null;

export type ReferenceModalState = {
  referenceModalOpen: boolean;
  element?: ElementType | null;
  reference?: Reference | null;
  referenceModalMode: ReferenceModalMode;
  /**
   *  主体详情来源
   *  character2video: 参考生创作页
   *  community: 社区，feed 流
   *  select: 主体选择器
   *  others: 其他
   *  share: 分享
   *  profile: 个人主页
   *  my_likes_page: 我的点赞
   *  post_reference: 社区投稿
   */
  referenceModalFrom?:
    | 'character2video'
    | 'reference2image'
    | 'community'
    | 'select'
    | 'others'
    | 'share'
    | 'my_likes_page'
    | 'my_published_page'
    | 'guest_profile'
    | 'profile'
    | 'post_reference'
    | null;
  /**
   *  主体详情类型
   *  share: 共享主体
   *  reference: 主体
   */
  referenceModalType?: 'share' | 'reference' | null;
  /**
   *  主体详情来源页面，主要用于埋点
   *  Home: Home页
   *  发现页：explore: 发现页
   *  reference_tool: 主体选择器
   *  reference_asset: 主体库
   *  reference_asset_details: 主体库的主体详情
   *  reference_tool_details: 主体选择器中的主体详情
   *  others: 其他
   */
  referenceModalFromPage?: FromPage;
  /**
   * 主体详情更新原因
   *  add: 添加
   *  update: 更新
   *  delete: 删除
   *  cancelSelect: 取消选择，用于h5参考生创作工具处
   *  forbidden: 禁止，主体删除、取消共享等场景
   *  like: 点赞/取消点赞，用于更新外面 feed 流的点赞数据
   *  copy: 复制，用于复制主体到个人主体库
   *  use: 填充到参考生工具中
   */
  referenceModalUpdateReason?:
    | {
        type: 'add';
      }
    | {
        type: 'update';
        reference: ElementType;
      }
    | {
        type: 'delete';
        reference?: ElementType;
        ids?: string[];
      }
    | {
        type: 'cancelSelect';
        reference: ElementType;
      }
    | {
        type: 'forbidden';
      }
    | {
        type: 'like';
        id: string;
        voted: boolean;
      }
    | {
        type: 'copy';
        id: string;
        count: number;
      }
    | {
        type: 'use';
      }
    | {
        type: 'collect';
        id: string;
        collect_count: number;
        collected: boolean;
      };
};

export interface ReferenceModalStore extends ReferenceModalState {
  /**
   * 更新主体详情模式
   * @param mode 主体详情模式
   */
  updateReferenceModalMode: (mode: ReferenceModalState['referenceModalMode']) => void;
  /**
   * 更新主体详情类型
   * @param type 主体详情类型
   */
  updateReferenceModalType: (type: ReferenceModalState['referenceModalType']) => void;
  updateReference: (reference: Reference) => void;
  openReferenceModalWithData: (data: ReferenceModalState) => void;
  /**
   * 关闭主体详情模态框
   * @param reason 主体详情更新原因
   */
  closeReferenceModal: (reason?: ReferenceModalState['referenceModalUpdateReason']) => void;
  /**
   * 更新主体详情更新原因
   * @param reason 主体详情更新原因
   */
  updateReferenceModalUpdateReason: (reason?: ReferenceModalState['referenceModalUpdateReason']) => void;
  /**
   * 更新主体详情来源
   * @param from 主体详情来源
   */
  updateReferenceModalFrom: (from: ReferenceModalState['referenceModalFrom']) => void;
  /**
   * 更新主体详情来源页面
   * @param referenceModalFromPage 主体详情来源页面
   */
  updateReferenceModalFromPage: (referenceModalFromPage: ReferenceModalState['referenceModalFromPage']) => void;
}

const initialState: ReferenceModalState = {
  referenceModalOpen: false,
  referenceModalMode: null,
  referenceModalType: null,
  referenceModalFrom: null,
  element: null,
  reference: null,
  referenceModalUpdateReason: undefined,
};

export const createReferenceModalSlice: StateCreator<ReferenceModalStore, [], [], ReferenceModalStore> = (
  set,
  get,
) => ({
  ...initialState,
  openReferenceModalWithData: (data: ReferenceModalState) => {
    if (data.reference?.element) {
      data.element = {
        id: data.reference.element.id,
        modality: 'image',
        version: data.reference.element.version,
        creator_id: data.reference.element.creator_id,
      };
    }
    set(data);
  },

  updateReference: (reference: Reference) => {
    set({ reference });
  },

  updateReferenceModalType: (referenceModalType: ReferenceModalState['referenceModalType']) => {
    set({ referenceModalType });
  },

  updateReferenceModalMode: (referenceModalMode: ReferenceModalState['referenceModalMode']) => {
    set({ referenceModalMode });
  },
  closeReferenceModal: (reason?: ReferenceModalState['referenceModalUpdateReason']) => {
    set({ referenceModalOpen: false, referenceModalUpdateReason: reason ?? get().referenceModalUpdateReason });
  },
  updateReferenceModalUpdateReason: (referenceModalUpdateReason: ReferenceModalState['referenceModalUpdateReason']) => {
    set({ referenceModalUpdateReason });
  },
  updateReferenceModalFrom: (referenceModalFrom: ReferenceModalState['referenceModalFrom']) => {
    set({ referenceModalFrom });
  },
  updateReferenceModalFromPage: (referenceModalFromPage: ReferenceModalState['referenceModalFromPage']) => {
    set({ referenceModalFromPage });
  },
});
